import React, { useEffect, useState } from "react";
import { Loader2, Wrench, Camera, Fuel, DollarSign, Briefcase, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError, fetchPhotoBlob } from "../lib/api";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);
import { fmtDate } from "../lib/dateFormat";

export function AssetJobsTab({ assetId }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get(`/assets/${assetId}/jobs`).then(r => setRows(r.data)).catch(e => setErr(formatApiError(e)));
    }, [assetId]);
    if (err) return <div className="text-sm text-red-300" data-testid="a360-jobs-error">{err}</div>;
    if (rows == null) return <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (rows.length === 0) return <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="a360-jobs-empty">{dict.asset360.jobs.empty}</div>;
    return (
        <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="a360-jobs-table">
            <table className="min-w-full text-sm">
                <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-400">
                    <tr>
                        <th className="text-left p-3">{dict.asset360.jobs.cols.code}</th>
                        <th className="text-left p-3">{dict.asset360.jobs.cols.client}</th>
                        <th className="text-left p-3">{dict.asset360.jobs.cols.description}</th>
                        <th className="text-left p-3">{dict.asset360.jobs.cols.dates}</th>
                        <th className="text-left p-3">{dict.asset360.jobs.cols.status}</th>
                        <th className="text-right p-3">{dict.asset360.jobs.cols.revenue}</th>
                        <th className="p-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(j => (
                        <tr key={j.id} className="border-t border-slate-800" data-testid={`a360-job-${j.id}`}>
                            <td className="p-3 font-mono text-xs text-brand-orange">{j.code}</td>
                            <td className="p-3">{j.client_name || "—"}</td>
                            <td className="p-3 max-w-xs truncate">{j.description}</td>
                            <td className="p-3 text-xs text-slate-400">{fmtDate(j.start_date)} {j.end_date ? `→ ${fmtDate(j.end_date)}` : ""}</td>
                            <td className="p-3"><span className="px-2 h-5 text-[10px] rounded-full bg-slate-800 border border-slate-700 inline-flex items-center uppercase tracking-wider">{j.status}</span></td>
                            <td className="p-3 text-right font-mono text-emerald-300">{fmtMXN(j.revenue_mxn)}</td>
                            <td className="p-3 text-right">
                                <Link to={`/trabajos/${j.id}`} className="text-xs text-brand-orange hover:underline">{dict.asset360.jobs.viewDetail} →</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function AssetMaintenanceTab({ assetId }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get(`/assets/${assetId}/maintenance-logs`).then(r => setRows(r.data)).catch(e => setErr(formatApiError(e)));
    }, [assetId]);
    if (err) return <div className="text-sm text-red-300">{err}</div>;
    if (rows == null) return <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                    <Wrench size={12} className="text-brand-orange"/> {rows.length} {dict.asset360.tabs.maintenance.toLowerCase()}
                </div>
                <Link to={`/mantenimiento?tab=logs&new=${assetId}`} data-testid="a360-maint-add"
                    className="text-xs text-brand-orange hover:underline">{dict.asset360.maintenance.addNew}</Link>
            </div>
            {rows.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="a360-maint-empty">{dict.asset360.maintenance.empty}</div>
            ) : (
                <div className="rounded-xl border border-slate-800 overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="a360-maint-table">
                        <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-400">
                            <tr>
                                <th className="text-left p-3">{dict.asset360.maintenance.cols.date}</th>
                                <th className="text-left p-3">{dict.asset360.maintenance.cols.kind}</th>
                                <th className="text-left p-3">{dict.asset360.maintenance.cols.description}</th>
                                <th className="text-left p-3">{dict.asset360.maintenance.cols.vendor}</th>
                                <th className="text-right p-3">{dict.asset360.maintenance.cols.cost}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(m => (
                                <tr key={m.id} className="border-t border-slate-800">
                                    <td className="p-3 text-slate-400">{fmtDate(m.date)}</td>
                                    <td className="p-3 uppercase text-[11px] text-slate-300">{m.kind}</td>
                                    <td className="p-3 max-w-md truncate">{m.description}</td>
                                    <td className="p-3 text-slate-400">{m.vendor || "—"}</td>
                                    <td className="p-3 text-right font-mono text-emerald-300">{fmtMXN(m.total_cost_mxn)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function PhotoBlob({ id }) {
    const [src, setSrc] = useState(null);
    useEffect(() => { let alive = true; fetchPhotoBlob(id).then(u => { if (alive) setSrc(u); }).catch(()=>{}); return () => { alive = false; }; }, [id]);
    if (!src) return <div className="aspect-square rounded-lg bg-slate-900 animate-pulse"/>;
    return <img src={src} alt="" className="aspect-square w-full object-cover rounded-lg border border-slate-800"/>;
}

export function AssetPhotosTab({ assetId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get(`/assets/${assetId}/photos`).then(r => setRows(r.data)).catch(() => setRows([]));
    }, [assetId]);
    if (rows == null) return <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (rows.length === 0) return <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="a360-photos-empty">{dict.asset360.photos.empty}</div>;
    return (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="a360-photos-grid">
            {rows.map(p => (
                <div key={p.id} className="space-y-1" data-testid={`a360-photo-${p.id}`}>
                    <PhotoBlob id={p.photo_id}/>
                    <div className="text-[10px] text-slate-500 truncate">{p.job_code} · {p.moment}</div>
                </div>
            ))}
        </div>
    );
}

export function AssetFuelTab({ assetId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/assets/${assetId}/fuel-logs`).then(r => setData(r.data)).catch(() => setData({ rows: [] }));
    }, [assetId]);
    if (data == null) return <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
                <KPI label={dict.asset360.fuel.kpiLiters} value={`${(data.total_liters || 0).toFixed(1)} L`}/>
                <KPI label={dict.asset360.fuel.kpiCost} value={fmtMXN(data.total_cost_mxn)}/>
                <KPI label={dict.asset360.fuel.kpiAvgKml} value={data.avg_kml != null ? `${data.avg_kml} km/L` : "—"}/>
            </div>
            {data.rows.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="a360-fuel-empty">{dict.asset360.fuel.empty}</div>
            ) : (
                <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="a360-fuel-table">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-400">
                            <tr>
                                <th className="text-left p-3">{dict.asset360.fuel.cols.date}</th>
                                <th className="text-right p-3">{dict.asset360.fuel.cols.liters}</th>
                                <th className="text-right p-3">{dict.asset360.fuel.cols.cost}</th>
                                <th className="text-right p-3">{dict.asset360.fuel.cols.kml}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map(f => (
                                <tr key={f.id} className="border-t border-slate-800">
                                    <td className="p-3 text-slate-400">{fmtDate(f.date)}</td>
                                    <td className="p-3 text-right font-mono">{f.liters?.toFixed(1)}</td>
                                    <td className="p-3 text-right font-mono text-emerald-300">{fmtMXN(f.total_cost_mxn)}</td>
                                    <td className="p-3 text-right font-mono text-slate-300">{f.computed?.kml != null ? f.computed.kml.toFixed(1) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function KPI({ label, value }) {
    return (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 text-lg font-display font-bold text-slate-100">{value}</div>
        </div>
    );
}

export function AssetCostsTab({ assetId }) {
    const today = new Date();
    const ytdStart = new Date(today.getFullYear(), 0, 1);
    const [range, setRange] = useState({ from: ytdStart.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) });
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/assets/${assetId}/cost-summary`, { params: { from: range.from, to: range.to } })
            .then(r => setData(r.data)).catch(() => setData(null));
    }, [assetId, range]);
    return (
        <div className="space-y-4" data-testid="a360-costs">
            <div className="flex flex-wrap gap-2 items-end p-3 rounded-xl bg-slate-900/40 border border-slate-800">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.asset360.costs.from}</label>
                    <input type="date" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} data-testid="a360-costs-from"
                        className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{dict.asset360.costs.to}</label>
                    <input type="date" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} data-testid="a360-costs-to"
                        className="h-9 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </div>
            </div>
            {data == null ? (
                <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <KPI label={dict.asset360.costs.fuel} value={fmtMXN(data.fuel_mxn)}/>
                    <KPI label={dict.asset360.costs.maintenance} value={fmtMXN(data.maintenance_mxn)}/>
                    <KPI label={dict.asset360.costs.tolls} value={fmtMXN(data.tolls_mxn)}/>
                    <KPI label={dict.asset360.costs.financing} value={fmtMXN(data.financing_mxn)}/>
                    <div className="rounded-xl border border-brand-orange/40 bg-brand-orange/5 p-3" data-testid="a360-costs-total">
                        <div className="text-[10px] uppercase tracking-wider text-brand-orange">{dict.asset360.costs.total}</div>
                        <div className="mt-1 text-lg font-display font-bold text-brand-orange">{fmtMXN(data.total_mxn)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const ASSET_360_TABS = [
    { key: "jobs", icon: Briefcase, Component: AssetJobsTab },
    { key: "maintenance", icon: Wrench, Component: AssetMaintenanceTab },
    { key: "photos", icon: Camera, Component: AssetPhotosTab },
    { key: "fuel", icon: Fuel, Component: AssetFuelTab },
    { key: "costs", icon: DollarSign, Component: AssetCostsTab },
];
